import {
  DataTypeDefinition,
  FirstDataRenderedEvent,
  GridApi,
  ICellRendererParams,
  IRowNode,
  RefreshCellsParams,
  ValueFormatterLiteParams,
  ValueGetterParams,
  ValueParserLiteParams,
  ValueSetterParams,
} from 'ag-grid-community'

import { format, isValid, parse, parseISO } from 'date-fns'
import { ProductStatus } from '@skop-app/lib-types'
import {
  extractIdLike,
  extractOptionValue,
  getCellEditorProperty,
  isReferencesView,
  markRemovedPropertyEntry,
  mustPersistOnlyInProperties,
  parseEditorScalar,
  resolveCanonicalFieldStorageKey,
  setNestedTopLevelValue,
} from '@/domain/product/persistence/propertyRules'

const displayOnFirstColumn = (params: ICellRendererParams) => {
  const displayedColumns = params.api.getAllDisplayedColumns()
  return (
    (displayedColumns?.length || 0) > 1 &&
    displayedColumns[1]?.getColId() === params.column?.getColId()
  )
}

export const groupCellRendererSelector = (params: ICellRendererParams) => {
  const isFirstColumn = displayOnFirstColumn(params)
  if (isFirstColumn) {
    return {
      component: 'agGroupCellRenderer',
    }
  }
}

// -----------------------------------------------------------------------------
// Canonical row <-> grid adapters
// -----------------------------------------------------------------------------
/**
 * Adapter layer used by column definitions to bridge between the canonical
 * row shape (base fields + dynamic properties[]) and AG Grid's cell APIs.
 *
 * Why:
 * - Avoids keeping a separate flattened view model in memory
 * - Keeps edits localized and cheap (mutate the canonical row directly)
 * - Decouples label mapping (lookups) from the stored IDs
 * - Scales with schema: new fields auto-wire through the same adapters
 */

/**
 * Minimal row shape the adapters expect. We only rely on `properties[]` and
 * a cached non-enumerable `__propIndex` for O(1) key lookups.
 */
export type UnknownRow = Record<string, unknown> & {
  id?: unknown
  properties?: Array<{ key: string; values?: unknown; type?: string }>
  __propIndex?: Record<string, number>
}

/**
 * Create a fast key -> index map on the row's properties array.
 * Non-enumerable so it won't leak into serialization or grid columns.
 */
export function ensurePropIndex(row: UnknownRow, force = false) {
  if (!row) return
  if (!force && Object.prototype.hasOwnProperty.call(row, '__propIndex')) return

  const index: Record<string, number> = {}
  const list = Array.isArray(row.properties) ? row.properties : []
  for (let i = 0; i < list.length; i += 1) {
    const k = list[i]?.key
    if (k) index[k] = i
  }

  if (!Object.prototype.hasOwnProperty.call(row, '__propIndex')) {
    Object.defineProperty(row, '__propIndex', {
      value: index,
      enumerable: false,
      writable: true,
    })
    return
  }

  row.__propIndex = index
}

function toSerializedValues(value: unknown): string[] {
  return asArray(value)
    .map(extractOptionValue)
    .map((item): string | null => {
      if (typeof item === 'string') return item
      if (typeof item === 'number') return String(item)
      if (typeof item === 'boolean') return item ? 'true' : 'false'
      return null
    })
    .filter((item): item is string => item !== null)
}

/**
 * Normalize arbitrary input into an array. Used to keep LIST values
 * consistently as arrays in the canonical model.
 */
function asArray<T>(value: T | T[] | null | undefined): T[] {
  if (Array.isArray(value)) return value
  if (value == null) return []
  return [value]
}

/**
 * Coerce editor input into a canonical value for the given type.
 * - LIST: always an array
 * - BOOLEAN: strict boolean
 * - default: passthrough with null for empty
 */
function coerceForType(newValue: unknown, type?: string) {
  switch (type) {
    case 'BOOLEAN':
      return String(newValue) === 'true'
    case 'LIST':
      return asArray(newValue ?? null)
    default:
      return newValue ?? null
  }
}

/**
 * Safely read nested top-level fields using dot notation (e.g. "quantity.number").
 */
function getNestedValue(path: string, obj: unknown): unknown {
  if (!obj) return undefined
  const parts = path.split('.')
  let cur: unknown = obj as Record<string, unknown>
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[part]
  }
  return cur
}

/**
 * Normalize raw value coming from either properties[] or top-level to what the grid expects.
 */
function normalizeGetterValue(raw: unknown, type?: string) {
  if (type === 'LIST') return Array.isArray(raw) ? raw : asArray(raw ?? null)
  return Array.isArray(raw) ? (raw[0] ?? null) : (raw ?? null)
}

export function readCanonicalRowFieldValue(
  row: UnknownRow | undefined,
  key: string,
  type?: string,
): unknown {
  if (!row) return null

  const storageKey = resolveCanonicalFieldStorageKey(key)
  ensurePropIndex(row)
  const readProperty = (propertyKey: string) => {
    const idx = row.__propIndex?.[propertyKey]
    return idx != null && Array.isArray(row.properties)
      ? row.properties[idx]
      : undefined
  }
  const prop = readProperty(storageKey)

  // Priority: if a property entry exists, it overrides top-level (even when empty)
  if (prop) {
    return normalizeGetterValue(prop?.values, type)
  }

  const topLevel = getNestedValue(storageKey, row)
  if (topLevel !== undefined) {
    return normalizeGetterValue(topLevel, type)
  }

  if (storageKey === key) {
    return normalizeGetterValue(topLevel, type)
  }

  const legacyProp = readProperty(key)
  if (legacyProp) {
    return normalizeGetterValue(legacyProp?.values, type)
  }

  const legacyTopLevel = getNestedValue(key, row)
  return normalizeGetterValue(legacyTopLevel, type)
}

/**
 * Build a valueGetter for a specific property key/type.
 * Reads from `properties[]` using the row's `__propIndex`.
 * For LIST returns arrays, otherwise single value.
 */
export function makeValueGetter(key: string, type?: string) {
  return (p: ValueGetterParams) => {
    if (p?.node?.group) {
      const aggValue = p.node.aggData?.[key]
      return aggValue !== undefined
        ? normalizeGetterValue(aggValue, type)
        : null
    }

    return readCanonicalRowFieldValue(p?.data, key, type)
  }
}

export const defaultValueGetter = (params: ValueGetterParams) => {
  const field = params.colDef?.field as string
  const cellEditorParams = params.colDef?.cellEditorParams
  return makeValueGetter(field, cellEditorParams?.property?.type)(params)
}

export const defaultValueSetter = (params: ValueSetterParams) => {
  const field = params.colDef?.field as string
  const cellEditorParams = params.colDef?.cellEditorParams
  return makeValueSetter(field, cellEditorParams?.property?.type)(params)
}

/** Keep generated Tree Data parents data-less after AG Grid 35 creates editor data. */
export const withSyntheticTreeGroupDataCleanup = <TData, TValue, TContext>(
  setter: (params: ValueSetterParams<TData, TValue, TContext>) => boolean,
) => {
  return (params: ValueSetterParams<TData, TValue, TContext>): boolean => {
    try {
      return setter(params)
    } finally {
      const { node, api } = params
      // AG Grid treats a filler with data as a real parent on its next group
      // pass, which produces false cycles and flattens the tree.
      if (
        node?.group &&
        node.sourceRowIndex < 0 &&
        api.getGridOption('treeData')
      ) {
        node.data = undefined
      }
    }
  }
}

/**
 * Immutable helper: Clone the row and update the property without mutating the original.
 */
export function updatePropertyInClone(
  rowClone: UnknownRow,
  key: string,
  newValue: unknown,
  type?: string,
) {
  const propertiesClone = [...(rowClone.properties ?? [])] // Clone array
  const idx = propertiesClone.findIndex((p) => p.key === key)
  const coerced = coerceForType(newValue, type)
  const serializedValues = toSerializedValues(coerced)

  if (idx === -1) {
    if (serializedValues.length === 0) {
      rowClone.properties = propertiesClone
      ensurePropIndex(rowClone, true)
      return
    }

    propertiesClone.push({
      key,
      values: serializedValues,
      type,
    })
  } else if (serializedValues.length === 0) {
    propertiesClone.splice(idx, 1)
  } else {
    propertiesClone[idx] = {
      ...propertiesClone[idx],
      values: serializedValues,
    }
  }

  rowClone.properties = propertiesClone
  ensurePropIndex(rowClone, true) // Recompute index on clone
}

function removePropertyEntryFromClone(rowClone: UnknownRow, key: string) {
  if (!Array.isArray(rowClone.properties)) return

  const propertiesClone = rowClone.properties.filter(
    (property) => property?.key !== key,
  )
  if (propertiesClone.length === rowClone.properties.length) return

  rowClone.properties = propertiesClone
  ensurePropIndex(rowClone, true)
  markRemovedPropertyEntry(rowClone, key)
}

function removeCanonicalPropertyEntriesFromClone(
  rowClone: UnknownRow,
  key: string,
  storageKey: string,
) {
  removePropertyEntryFromClone(rowClone, storageKey)
  if (storageKey !== key) {
    removePropertyEntryFromClone(rowClone, key)
  }
}

export type GroupValuePropagator = (
  params: ValueSetterParams<any, any, any>,
  fallbackField?: string,
) => boolean

/** Source value used when a group row fans out an edit to its descendant leaves. */
export const GROUP_PROPAGATION_SOURCE = 'groupPropagation'

export function makeValueSetter(
  key: string,
  type?: string,
  groupValuePropagator: GroupValuePropagator = propagateGroupValue,
  nestedOptions: Parameters<typeof setNestedTopLevelValue>[3] = {},
) {
  return (p: ValueSetterParams) => {
    const { data, newValue, oldValue } = p
    const row = data as UnknownRow
    if (!row) return false
    if (oldValue === newValue) return false
    const storageKey = resolveCanonicalFieldStorageKey(key)

    if (groupValuePropagator(p as ValueSetterParams<any, any, any>, key)) {
      return true
    }

    const editorMeta = getCellEditorProperty(p.colDef, type)
    const view = editorMeta.view
    const resolvedType = editorMeta.type

    if (isReferencesView(view)) {
      updatePropertyInClone(row, storageKey, newValue, resolvedType)
      return true
    }

    if (mustPersistOnlyInProperties({ key: storageKey, type: resolvedType })) {
      updatePropertyInClone(row, storageKey, newValue, resolvedType)
      return true
    }

    if (setNestedTopLevelValue(row, storageKey, newValue, nestedOptions)) {
      removeCanonicalPropertyEntriesFromClone(row, key, storageKey)
      return true
    }

    if (resolvedType === 'DYNAMIC') {
      row[storageKey] = extractIdLike(newValue) ?? null
      removeCanonicalPropertyEntriesFromClone(row, key, storageKey)
      return true
    }

    const scalar = parseEditorScalar(newValue)
    row[storageKey] = scalar.kind === 'empty' ? null : scalar.value
    removeCanonicalPropertyEntriesFromClone(row, key, storageKey)
    return true
  }
}

export function propagateGroupValue<TData, TValue, TContext>(
  params: ValueSetterParams<TData, TValue, TContext>,
  fallbackField?: string,
): boolean {
  const { node, colDef, newValue } = params
  if (!node?.group || colDef?.aggFunc === 'sum') return false

  const columnKey =
    (typeof colDef?.colId === 'string' && colDef.colId.length > 0
      ? colDef.colId
      : undefined) ??
    (typeof colDef?.field === 'string' ? colDef.field : undefined) ??
    fallbackField
  if (!columnKey) return false

  const leafChildren = [...(node.allLeafChildren ?? [])]
  if (leafChildren.length === 0) return false

  leafChildren.forEach((child) => {
    // Route through AG Grid's pipeline so leaf valueSetters stay authoritative.
    child.setDataValue(columnKey, newValue as unknown, GROUP_PROPAGATION_SOURCE)
  })
  return true
}

/**
 * Data type definitions for the grid, override the pre-defined cell data type dataTypeDefinitions
 * Specifically for dateTimeString, we want to handle ISO strings and human readable strings
 * @see https://www.ag-grid.com/react-data-grid/cell-data-types/#overriding-the-pre-defined-cell-data-type-definitions
 */

const isoDateTimeRegex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/i
const humanDateTimeMask = 'dd-MM-yyyy HH:mm:ss'

/**
 * Helper to determine if a property key represents a numeric field for aggregation.
 */
export function isNumericField(key: string): boolean {
  const numericKeys = [
    'price.amount',
    'quantity.number',
    'dimension.height',
    'dimension.length',
    'dimension.width',
    'weight',
    'dimensionRange',
  ]
  return numericKeys.some(
    (numericKey) => key.includes(numericKey) || key === numericKey,
  )
}

export const dataTypeDefinitions: { [key: string]: DataTypeDefinition } = {
  dateTimeString: {
    baseDataType: 'dateString',
    extendsDataType: 'dateTimeString',

    // Accept either our human format or an ISO string when the user edits
    valueParser: (params: ValueParserLiteParams<ProductStatus, string>) => {
      const v = params.newValue?.trim()
      if (!v) return null

      // If user typed our display format -> turn into ISO for storage
      const asHuman = parse(v, humanDateTimeMask, new Date())
      if (isValid(asHuman)) {
        return new Date(asHuman.getTime()).toISOString()
      }

      // If user pasted an ISO string -> keep it
      if (isoDateTimeRegex.test(v) && isValid(parseISO(v))) {
        return v
      }

      return null // reject invalid text
    },

    // Identify values that are strings looking like ISO (or already human)
    dataTypeMatcher: (value: unknown) => {
      if (typeof value !== 'string') return false
      if (isoDateTimeRegex.test(value)) return true
      // Also match already-formatted human strings
      return /^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2}$/.test(value)
    },

    // Convert the underlying string to a Date for the grid/editor
    dateParser: (value: string | undefined) => {
      if (!value) return undefined

      if (isoDateTimeRegex.test(value)) {
        const d = parseISO(value)
        return isValid(d) ? d : undefined
      }

      // Support our human display format too
      const d = parse(value, humanDateTimeMask, new Date())
      return isValid(d) ? d : undefined
    },

    // Render to `03-06-2025 12:59:07`
    dateFormatter: (value: Date | undefined) => {
      if (!value) return undefined

      return format(value, humanDateTimeMask)
    },

    // Optional: if cells bind directly to string values, also supply valueFormatter
    valueFormatter: (
      params: ValueFormatterLiteParams<ProductStatus, string>,
    ) => {
      const v = params.value
      if (!v) return ''
      // If it’s ISO, render in our human format
      if (isoDateTimeRegex.test(v)) {
        const d = parseISO(v)
        return isValid(d) ? format(d, humanDateTimeMask) : ''
      }
      // If it’s already human, keep it
      if (/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2}$/.test(v)) {
        return v
      }
      return ''
    },
  },
}

export const selectNode = (params: FirstDataRenderedEvent, nodeId: string) => {
  const rowNode = params.api.getRowNode(nodeId)
  if (rowNode) {
    params.api.ensureNodeVisible(rowNode)
    params.api.deselectAll()
    params.api.flashCells({
      flashDuration: 1000,
      rowNodes: [rowNode],
    })
    rowNode.setSelected(true)
  }
}

export const resetSelection = (api: GridApi) => {
  api.deselectAll()
  api.clearCellSelection()
  api.clearFocusedCell()
}

export const refreshRows = (api: GridApi, rowsToRefresh: IRowNode[]) => {
  const params: RefreshCellsParams = {
    rowNodes: rowsToRefresh,
    force: true,
  }
  api.refreshCells(params)
}
